/**
 * VibrancyPanel — macOS-style vibrancy blur surface
 *
 * 용도: 사이드바, 모달 배경, 드롭다운, 시트 등.
 * 핵심: `backdrop-blur-2xl` + `saturate(180%)` 조합이 macOS 특유의
 *      "뒤 배경이 비쳐 보이되 채도는 살아있는" 느낌을 만든다.
 *
 * 구현:
 *  - 기본: `bg-white/60 dark:bg-black/55`
 *  - blur: `backdrop-blur-2xl` (tokens: 40px)
 *  - saturate: inline style 로 `backdrop-filter: saturate(180%)` 보완
 *  - hairline: 사이드바 variant 는 우측 0.5px border 자동
 *
 * 접근성:
 *  - 반투명 배경 위에서도 텍스트 대비는 WCAG AA 이상 (text-admin-primary 유지)
 *  - reduce-transparency 환경에선 자동으로 불투명 폴백
 */

import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '../utils/cn';

type Variant = 'sidebar' | 'sheet' | 'dropdown' | 'modal';

interface VibrancyPanelProps extends HTMLAttributes<HTMLDivElement> {
  /** 기본 `sheet`. 사이드바/드롭다운/모달 용도 변경 시 사용 */
  variant?: Variant;
  /** 하위 요소 */
  children: ReactNode;
}

const VARIANT_CLASS: Record<Variant, string> = {
  /** 사이드바: 반투명 + 우측 hairline */
  sidebar:
    'bg-[rgba(246,246,248,0.72)] dark:bg-[rgba(30,30,30,0.65)] border-r-[0.5px] border-[rgba(0,0,0,0.08)] dark:border-[rgba(255,255,255,0.12)]',
  /** 시트 · 팝업 패널: rounded + shadow */
  sheet:
    'bg-[rgba(255,255,255,0.60)] dark:bg-[rgba(30,30,30,0.55)] rounded-[12px] shadow-[0_0_0_0.5px_rgba(0,0,0,0.08),0_8px_24px_rgba(0,0,0,0.06)]',
  /** 드롭다운 · 팝오버 */
  dropdown:
    'bg-[rgba(255,255,255,0.72)] dark:bg-[rgba(40,40,42,0.72)] rounded-[8px] shadow-[0_0_0_0.5px_rgba(0,0,0,0.08),0_8px_24px_rgba(0,0,0,0.06)]',
  /** 모달 · Sheet 대형 */
  modal:
    'bg-[rgba(255,255,255,0.85)] dark:bg-[rgba(30,30,30,0.85)] rounded-[16px] shadow-[0_0_0_0.5px_rgba(0,0,0,0.1),0_24px_48px_rgba(0,0,0,0.16)]',
};

/** Vibrancy backdrop-filter — Safari prefix 보완 용 inline style */
const VIBRANCY_STYLE = {
  backdropFilter: 'blur(40px) saturate(180%)',
  WebkitBackdropFilter: 'blur(40px) saturate(180%)',
} as const;

export function VibrancyPanel({
  variant = 'sheet',
  className,
  children,
  style,
  ...rest
}: VibrancyPanelProps) {
  return (
    <div
      className={cn(VARIANT_CLASS[variant], className)}
      style={{ ...VIBRANCY_STYLE, ...style }}
      {...rest}
    >
      {children}
    </div>
  );
}

VibrancyPanel.displayName = 'VibrancyPanel';
